/**
 * Admin modal for linking additional billing accounts (Zoho customers)
 * to an existing portal user. Used for customers whose Hub records are
 * split across multiple locations but who bill as a single portal user
 * (e.g. BCN GLOBAL LOGISTICS).
 *
 * Flow:
 *  1. On open, GET /admin/customers/{pcid}/accounts to show currently
 *     linked locations at the top.
 *  2. Admin searches Zoho by name/address/email — GET /admin/zoho/search.
 *  3. Admin picks a result; we POST /admin/customers/{pcid}/link-account
 *     with the zoho_customer_id.
 *  4. Admin can also unlink previously admin-linked locations (the
 *     primary Hub-provisioned account is protected server-side).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  X, Search, Loader2, Plus, MapPin, CheckCircle2, AlertCircle, Trash2,
} from 'lucide-react';

import { adminApi } from '@/lib/api';

export default function AdminLinkLocationModal({ customer, onClose, onLinked }) {
  const [linked, setLinked] = useState(null);       // array of currently linked accts
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);     // array of Zoho search hits
  const [searching, setSearching] = useState(false);
  const [busyZohoId, setBusyZohoId] = useState(null);
  const [flash, setFlash] = useState(null);         // { type, msg }

  const loadLinked = useCallback(async () => {
    try {
      const { data } = await adminApi.listLinkedAccounts(customer.portal_customer_id);
      setLinked(data?.accounts || []);
    } catch (err) {
      setLinked([]);
      setFlash({ type: 'error', msg: err?.detail || 'Failed to load linked locations.' });
    }
  }, [customer.portal_customer_id]);

  useEffect(() => { loadLinked(); }, [loadLinked]);

  const doSearch = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!q || q.trim().length < 2) {
      setFlash({ type: 'error', msg: 'Type at least 2 characters to search.' });
      return;
    }
    setSearching(true);
    setResults(null);
    setFlash(null);
    try {
      const { data } = await adminApi.zohoSearch(q.trim());
      setResults(data?.results || []);
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Zoho search failed.' });
    } finally {
      setSearching(false);
    }
  }, [q]);

  const link = async (row) => {
    if (busyZohoId) return;
    setBusyZohoId(row.zoho_customer_id);
    setFlash(null);
    try {
      const { data } = await adminApi.linkAccount(
        customer.portal_customer_id,
        row.zoho_customer_id,
        `Linked ${row.display_name} to ${customer.business_name}`,
      );
      setFlash({
        type: 'success',
        msg: data?.already_linked
          ? `${row.display_name} was already linked.`
          : `Linked ${row.display_name} — the customer will now see this location on their dashboard.`,
      });
      await loadLinked();
      onLinked?.();
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Link failed.' });
    } finally {
      setBusyZohoId(null);
    }
  };

  const unlink = async (acct) => {
    if (busyZohoId) return;
    setBusyZohoId(acct.zoho_customer_id);
    setFlash(null);
    try {
      await adminApi.unlinkAccount(customer.portal_customer_id, acct.account_id);
      setFlash({ type: 'success', msg: `Unlinked ${acct.display_name}.` });
      await loadLinked();
      onLinked?.();
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Unlink failed.' });
    } finally {
      setBusyZohoId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="admin-link-location-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EDE5D4]">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#0B1930]" />
            <div>
              <h2 className="font-heading uppercase tracking-wide text-base text-[#0B1930]">
                Linked Locations
              </h2>
              <p className="text-[11px] text-[#4B5563]">{customer.business_name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#4B5563] hover:text-[#0B1930]"
            data-testid="admin-link-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {flash && (
            <div
              className={`mb-3 rounded-xl px-3 py-2 text-sm flex items-start gap-2 ${
                flash.type === 'success'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
              data-testid="admin-link-flash"
            >
              {flash.type === 'success'
                ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <span>{flash.msg}</span>
            </div>
          )}

          <section className="mb-5">
            <h3 className="text-[11px] uppercase tracking-wide text-[#4B5563] mb-2">
              Currently linked ({linked?.length ?? '…'})
            </h3>
            {linked === null && (
              <div className="text-xs text-[#4B5563] flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading…
              </div>
            )}
            {linked !== null && linked.length === 0 && (
              <p className="text-xs text-[#9CA3AF]">No linked billing accounts yet.</p>
            )}
            {linked !== null && linked.length > 0 && (
              <div className="rounded-xl border border-[#EDE5D4] divide-y divide-[#EDE5D4] overflow-hidden">
                {linked.map((a) => (
                  <div
                    key={a.account_id}
                    className="px-3 py-2.5 flex items-start justify-between gap-3"
                    data-testid={`admin-linked-row-${a.account_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0B1930] truncate">
                        {a.display_name}
                        {!a.admin_linked && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                            Primary
                          </span>
                        )}
                      </p>
                      {a.service_address && (
                        <p className="text-[11px] text-[#4B5563] truncate">{a.service_address}</p>
                      )}
                      <p className="text-[11px] text-[#9CA3AF]">
                        Zoho ID {a.zoho_customer_id} · ${a.current_balance.toFixed(2)} due
                      </p>
                    </div>
                    {a.admin_linked && (
                      <button
                        type="button"
                        onClick={() => unlink(a)}
                        disabled={busyZohoId === a.zoho_customer_id}
                        className="inline-flex items-center gap-1 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                        data-testid={`admin-unlink-${a.account_id}`}
                      >
                        {busyZohoId === a.zoho_customer_id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Trash2 className="h-3 w-3" />}
                        Unlink
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-wide text-[#4B5563] mb-2">
              Add another Zoho customer
            </h3>
            <form onSubmit={doSearch} className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search Zoho by name, address, or email…"
                  className="w-full rounded-full bg-white border border-[#EDE5D4] pl-9 pr-4 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                  data-testid="admin-link-search-input"
                />
              </div>
              <button
                type="submit"
                disabled={searching}
                className="rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
                data-testid="admin-link-search-submit"
              >
                {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Search'}
              </button>
            </form>

            {results === null && !searching && (
              <p className="text-xs text-[#9CA3AF]">Type a Zoho name / address / email above and hit Search.</p>
            )}
            {results !== null && results.length === 0 && (
              <p className="text-xs text-[#9CA3AF]">No Zoho customers matched.</p>
            )}
            {results !== null && results.length > 0 && (
              <div className="rounded-xl border border-[#EDE5D4] divide-y divide-[#EDE5D4] overflow-hidden">
                {results.map((r) => {
                  const alreadyLinked =
                    !!r.already_linked_account_id &&
                    (linked || []).some((a) => a.account_id === r.already_linked_account_id);
                  return (
                    <div
                      key={r.zoho_customer_id}
                      className="px-3 py-2.5 flex items-center justify-between gap-3"
                      data-testid={`admin-zoho-result-${r.zoho_customer_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0B1930] truncate">{r.display_name}</p>
                        <p className="text-[11px] text-[#4B5563] truncate">
                          {r.email || r.phone || '—'} · Zoho {r.zoho_customer_id}
                        </p>
                        {r.current_balance > 0 && (
                          <p className="text-[11px] text-[#B8925A]">${r.current_balance.toFixed(2)} due</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => link(r)}
                        disabled={busyZohoId === r.zoho_customer_id || alreadyLinked}
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-60 ${
                          alreadyLinked
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        }`}
                        data-testid={`admin-link-btn-${r.zoho_customer_id}`}
                      >
                        {busyZohoId === r.zoho_customer_id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Plus className="h-3 w-3" />}
                        {alreadyLinked ? 'Linked' : 'Link'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end px-5 py-3 border-t border-[#EDE5D4] bg-[#FAF7F0]">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
            data-testid="admin-link-modal-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
